import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useEntity, EntityDisplayName } from '@mainstage/plugin-catalog-react';
import { ResponseErrorPanel } from '@mainstage/core-components';
import { useApi } from '@mainstage/core-plugin-api';
import {
  WbCard,
  WbCardContent,
  WbTable,
  WbBadge,
  TableCellProps,
  isoDateToDisplayString,
  createActionsColumn,
} from '@agilelab/plugin-wb-platform';
import {
  entityToUrn,
  FeatureRequestListItem,
  FeatureRequestStatus,
  FeatureRequestPriority,
  FeatureRequestFilters,
  PaginatedResponse,
} from '@agilelab/plugin-wb-builder-common';
import { useFeatureRequests } from '../../hooks/useFeatureRequests';
import { useFeatureRequestMutations } from '../../hooks/useFeatureRequestMutations';
import { useSearchParams } from 'react-router-dom';
import {
  FeatureRequestFilters as FeatureRequestFiltersBar,
  FeatureRequestFilterValues,
} from './FeatureRequestFilters';
import { FeatureRequestDetail } from './FeatureRequestDetail';
import { FeatureRequestActionsColumn } from './FeatureRequestActionsColumn';
import { FeatureRequestDeleteDialog } from './FeatureRequestDeleteDialog';
import { FeatureRequestStatusBadge } from './FeatureRequestStatusBadge';
import { panelCatalogApiRef } from '../../api';

export interface FeatureRequestPanelProps {
  data?: PaginatedResponse<FeatureRequestListItem>;
  isLoading?: boolean;
  error?: Error | null;
  showActions?: boolean;
  filterComponent?: ReactNode;
  onRowClick?: (row: FeatureRequestListItem) => void;
  columns?: TableCellProps<FeatureRequestListItem>[];
  noDataLabel?: string;
  detailComponent?: ReactNode;
  pagination?: {
    count: number;
    limit: number;
    currentPage: number;
    rowsPerPageOptions?: number[];
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
  };
}

const PRIORITY_COLOR_MAP: Record<
  FeatureRequestPriority,
  'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'
> = {
  [FeatureRequestPriority.LOW]: 'info',
  [FeatureRequestPriority.MEDIUM]: 'warning',
  [FeatureRequestPriority.HIGH]: 'error',
  [FeatureRequestPriority.CRITICAL]: 'error',
};

/** Renders with externally-provided data — no entity/fetching hooks */
const ExternalFeatureRequestPanel = (props: FeatureRequestPanelProps) => {
  const {
    data,
    isLoading = false,
    error,
    filterComponent,
    onRowClick,
    columns: columnsProp,
    noDataLabel = 'No feature requests found.',
    detailComponent,
    pagination,
  } = props;

  const defaultColumns: TableCellProps<FeatureRequestListItem>[] =
    useMemo(() => {
      const cols: TableCellProps<FeatureRequestListItem>[] = [
        {
          headerName: 'Summary',
          field: 'summary',
          tooltip: true,
        },
        {
          headerName: 'Requester',
          field: 'requesterRef',
          fieldRender: row => (
            <EntityDisplayName
              entityRef={row.requesterRef}
              defaultKind="user"
              hideIcon
            />
          ),
        },
        {
          headerName: 'Date',
          field: 'createdAt',
          fieldRender: row =>
            isoDateToDisplayString(row.createdAt, {
              showHour: false,
              showMinute: false,
              showSecond: false,
            }),
          typographyProps: { style: { whiteSpace: 'nowrap' } },
        },
        {
          headerName: 'Status',
          field: 'status',
          fieldRender: row => <FeatureRequestStatusBadge status={row.status} />,
        },
        {
          headerName: 'Priority',
          field: 'priority',
          fieldRender: row => (
            <WbBadge color={PRIORITY_COLOR_MAP[row.priority]}>
              {row.priority}
            </WbBadge>
          ),
        },
      ];
      return cols;
    }, []);

  const columns = columnsProp ?? defaultColumns;

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <>
      <WbCard title="All requests">
        <WbCardContent>
          {filterComponent}
          <WbTable<FeatureRequestListItem>
            stickyHeader
            components={{
              tableLoader: { loading: isLoading },
              tableContent: {
                columns,
                rows: data?.items ?? [],
                onRowClick,
              },
            }}
            noDataLabel={noDataLabel}
            pagination={pagination}
          />
        </WbCardContent>
      </WbCard>
      {detailComponent}
    </>
  );
};

/** Original behavior — uses entity context and internal hooks */
const InternalFeatureRequestPanel = (_props: FeatureRequestPanelProps) => {
  const { entity } = useEntity();
  const projectUrn = entityToUrn(entity);
  const panelCatalogApi = useApi(panelCatalogApiRef);
  const [searchParams, setSearchParams] = useSearchParams();
  const { updateStatus } = useFeatureRequestMutations();
  const [deleteTarget, setDeleteTarget] =
    useState<FeatureRequestListItem | null>(null);

  const detailId = searchParams.get('detail');

  const filters: FeatureRequestFilters = useMemo(
    () => ({
      status: (searchParams.get('status') as FeatureRequestStatus) || undefined,
      priority:
        (searchParams.get('priority') as FeatureRequestPriority) || undefined,
      componentUrn: searchParams.get('componentUrn') || undefined,
      offset: Number(searchParams.get('offset') || '0'),
      limit: Number(searchParams.get('limit') || '20'),
    }),
    [searchParams],
  );

  const updateParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, val]) => {
            if (val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleFilterChange = useCallback(
    (filterValues: FeatureRequestFilterValues) => {
      updateParams({
        status: filterValues.status,
        priority: filterValues.priority,
        componentUrn: filterValues.componentUrn,
        offset: '0',
      });
    },
    [updateParams],
  );

  const handleRowClick = useCallback(
    (row: FeatureRequestListItem) => {
      updateParams({ detail: row.id });
    },
    [updateParams],
  );

  const handleBackToList = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('detail');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const { data, isLoading, error } = useFeatureRequests(projectUrn, filters);

  const columns: TableCellProps<FeatureRequestListItem>[] = useMemo(() => {
    const cols: TableCellProps<FeatureRequestListItem>[] = [
      {
        headerName: 'Summary',
        field: 'summary',
        minWidth: 300,
        tooltip: true,
      },
      {
        headerName: 'Requester',
        field: 'requesterRef',
        fieldRender: row => (
          <EntityDisplayName
            entityRef={row.requesterRef}
            defaultKind="user"
            hideIcon
          />
        ),
      },
      {
        headerName: 'Date',
        field: 'createdAt',
        fieldRender: row =>
          isoDateToDisplayString(row.createdAt, {
            showHour: false,
            showMinute: false,
            showSecond: false,
          }),
        typographyProps: { style: { whiteSpace: 'nowrap' } },
      },
      {
        headerName: 'Status',
        field: 'status',
        fieldRender: row => <FeatureRequestStatusBadge status={row.status} />,
      },
      {
        headerName: 'Priority',
        field: 'priority',
        fieldRender: row => (
          <WbBadge color={PRIORITY_COLOR_MAP[row.priority]}>
            {row.priority}
          </WbBadge>
        ),
      },
    ];

    cols.push(
      createActionsColumn({
        renderActions: row => (
          <FeatureRequestActionsColumn
            request={row}
            onView={handleRowClick}
            onChangeStatus={(r, status) =>
              updateStatus.mutate({ id: r.id, status })
            }
            onDelete={r => setDeleteTarget(r)}
          />
        ),
      }),
    );

    return cols;
  }, [handleRowClick, updateStatus]);

  if (error) {
    return <ResponseErrorPanel error={error as Error} />;
  }

  return (
    <>
      <WbCard title="All requests">
        <WbCardContent>
          <FeatureRequestFiltersBar
            values={{
              status: filters.status,
              priority: filters.priority,
              componentUrn: filters.componentUrn,
            }}
            onChange={handleFilterChange}
          />
          <WbTable<FeatureRequestListItem>
            stickyHeader
            components={{
              tableLoader: { loading: isLoading },
              tableContent: {
                columns,
                rows: data?.items ?? [],
                onRowClick: handleRowClick,
              },
            }}
            noDataLabel="No feature requests found for this system."
            pagination={{
              count: data?.total ?? 0,
              limit: filters.limit,
              currentPage: Math.floor(filters.offset / filters.limit),
              rowsPerPageOptions: [20, 50, 100],
              onPageChange: (page: number) =>
                updateParams({ offset: String(page * filters.limit) }),
              onRowsPerPageChange: (rowsPerPage: number) =>
                updateParams({ limit: String(rowsPerPage), offset: '0' }),
            }}
          />
        </WbCardContent>
      </WbCard>
      {deleteTarget && (
        <FeatureRequestDeleteDialog
          open
          featureRequestId={deleteTarget.id}
          summary={deleteTarget.summary}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => setDeleteTarget(null)}
        />
      )}
      <FeatureRequestDetail
        featureRequestId={detailId}
        open={!!detailId}
        onClose={handleBackToList}
        fetchAttachmentBlob={(frId, attachmentId) =>
          panelCatalogApi.downloadFeatureRequestAttachment(frId, attachmentId)
        }
      />
    </>
  );
};

export const FeatureRequestPanel = (props: FeatureRequestPanelProps = {}) => {
  const isExternalData =
    props.data !== undefined ||
    props.isLoading !== undefined ||
    props.error !== undefined;

  if (isExternalData) {
    return <ExternalFeatureRequestPanel {...props} />;
  }

  return <InternalFeatureRequestPanel {...props} />;
};
